export type SupportedCurrency = 'USD' | 'EUR' | 'GBP' | 'INR' | 'BRL';

export interface CurrencyConfig {
  code: SupportedCurrency;
  symbol: string;
  locale: string;
}

export const CURRENCY_CONFIGS: Record<SupportedCurrency, CurrencyConfig> = {
  USD: {
    code: 'USD',
    symbol: '$',
    locale: 'en-US',
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    locale: 'de-DE',
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    locale: 'en-GB',
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    locale: 'en-IN',
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    locale: 'pt-BR',
  },
};

// Fixed prices for each plan in each currency
export const PLAN_PRICES: Record<
  SupportedCurrency,
  {
    pro: { original: number; monthly: number; annual: number };
    unlimited: { original: number; monthly: number; annual: number };
  }
> = {
  USD: {
    pro: { original: 49, monthly: 29, annual: 23 },
    unlimited: { original: 150, monthly: 79, annual: 63 },
  },
  EUR: {
    pro: { original: 49, monthly: 25, annual: 19 },
    unlimited: { original: 130, monthly: 69, annual: 54 },
  },
  GBP: {
    pro: { original: 39, monthly: 22, annual: 18 },
    unlimited: { original: 120, monthly: 59, annual: 49 },
  },
  INR: {
    pro: { original: 4999, monthly: 2999, annual: 2499 },
    unlimited: { original: 15000, monthly: 7999, annual: 6399 },
  },
  BRL: {
    pro: { original: 260, monthly: 159, annual: 125 },
    unlimited: { original: 800, monthly: 419, annual: 329 },
  },
};

export const TOP_UP_PRICES: Record<
  'USD' | 'INR',
  { price: number; originalPrice: number; messages: number; isPopular?: boolean }[]
> = {
  USD: [
    { price: 9, originalPrice: 12, messages: 50 },
    { price: 15, originalPrice: 19, messages: 100 },
    { price: 29, originalPrice: 35, messages: 200, isPopular: true },
    { price: 39, originalPrice: 50, messages: 300 },
  ],
  INR: [
    { price: 999, originalPrice: 1299, messages: 50 },
    { price: 1499, originalPrice: 1999, messages: 100 },
    { price: 2999, originalPrice: 3999, messages: 200, isPopular: true },
    { price: 3999, originalPrice: 5099, messages: 300 },
  ],
};

// Map country codes to currencies
export const COUNTRY_TO_CURRENCY: Record<string, SupportedCurrency> = {
  // United States
  US: 'USD',

  // Eurozone countries
  AT: 'EUR',
  BE: 'EUR',
  CY: 'EUR',
  EE: 'EUR',
  FI: 'EUR',
  FR: 'EUR',
  DE: 'EUR',
  GR: 'EUR',
  IE: 'EUR',
  IT: 'EUR',
  LV: 'EUR',
  LT: 'EUR',
  LU: 'EUR',
  MT: 'EUR',
  NL: 'EUR',
  PT: 'EUR',
  SK: 'EUR',
  SI: 'EUR',
  ES: 'EUR',

  // United Kingdom
  GB: 'GBP',

  // India
  IN: 'INR',

  // Brazil
  BR: 'BRL',
};

export function getCurrencyFromCountry(countryCode: string): SupportedCurrency {
  return COUNTRY_TO_CURRENCY[countryCode.toUpperCase()] || 'USD';
}

export function getPrices(currency: SupportedCurrency) {
  return PLAN_PRICES[currency];
}

export function getTopUpPrices(currency: 'USD' | 'INR') {
  return TOP_UP_PRICES[currency];
}

export function formatPrice(price: number, currency: SupportedCurrency = 'USD'): string {
  const config = CURRENCY_CONFIGS[currency];

  // For INR and BRL, show no decimals and format with commas
  if (currency === 'INR' || currency === 'BRL') {
    return `${config.symbol}${Math.round(price).toLocaleString('en-US')}`;
  }

  // For others, show no decimals
  return `${config.symbol}${Math.round(price)}`;
}
